import {Component, Inject, OnInit, TrackByFunction} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    AnalystRate,
    AnalystReason,
    AnalystSection,
    AnalyticAgentApiService,
    AnalyticAgentFeedback, AnalyticAgentReportFeedbackResponse,
    AnalyticAgentReportResponse,
    AnalyticAgentReportType, ReferenceBookResponse, ReferenceBookType,
} from '@npck/af-frontend-api';
import {Spinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest} from 'rxjs';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';

@Component({
    selector: 'af-analytic-agent-report-feedback-dialog',
    standalone: false,
    templateUrl: './analytic-agent-report-feedback-dialog.component.html',
    styleUrl: './analytic-agent-report-feedback-dialog.component.scss',
})
export class AnalyticAgentReportFeedbackDialogComponent implements OnInit {
    Spinner = Spinner;
    form: FormGroup<AnalyticAgentReportFeedbacksForm>;
    feedbacks: AnalyticAgentReportFeedbackResponse[];
    dialogTitle: string;
    dialogSubtitle: string;
    sectionDictionary: ReferenceBookResponse[] = [];
    rateDictionary: ReferenceBookResponse[] = [];
    reasonDictionary: ReferenceBookResponse[] = [];

    private sections: AnalystSection[] = [
        AnalystSection.INCIDENT_INFO,
        AnalystSection.FRAUD_CONTACTS,
        AnalystSection.INCIDENT_TRANSACTIONS,
        AnalystSection.TRANSACTION_FLOW,
        AnalystSection.LINKS,
        AnalystSection.AI_ANALYSIS,
        AnalystSection.RECOMMENDATIONS
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: AnalyticAgentReportFeedbackDialogData,
        public dialogRef: MatDialogRef<AnalyticAgentReportFeedbackDialogComponent>,
        private formBuilder: FormBuilder,
        private analyticAgentApiService: AnalyticAgentApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private snackBarService: SnackbarService,
    ) {}

    get isPdfReport(): boolean {
        return this.dialogData?.report?.reportType === AnalyticAgentReportType.PDF;
    }

    get isReadOnly(): boolean {
        return this.dialogData?.readonly;
    }

    ngOnInit(): void {
        this.dialogTitle = 'i18n_af_incidentAnalyticAgentFeedbackDialogTitle#' + this.dialogData?.report?.reportType;
        this.dialogSubtitle = 'i18n_af_incidentAnalyticAgentFeedbackDialogSubtitle#' + this.dialogData?.report?.reportType;

        if (this.isReadOnly) {
            combineLatest([
                this.dictionaryHolderService.getReference(ReferenceBookType.AI_ANALYST_SECTION),
                this.dictionaryHolderService.getReference(ReferenceBookType.AI_ANALYST_RATE),
                this.dictionaryHolderService.getReference(ReferenceBookType.AI_ANALYST_REASON),
                this.analyticAgentApiService.getIncidentReportFeedbacks(this.dialogData?.report?.id),
            ]).pipe().subscribe(([sections, rates, reasons, response]) => {
                this.sectionDictionary = sections;
                this.rateDictionary = rates;
                this.reasonDictionary = reasons;
                this.feedbacks = response;
                this.buildForm();
            });
        } else {
            this.dictionaryHolderService.getReference(ReferenceBookType.AI_ANALYST_SECTION).pipe()
                .subscribe((sections) => {
                    this.sectionDictionary = sections;
                    this.buildForm();
                });
        }
    }

    translateSection(value: AnalystSection): string {
        return findValueInDictionary(this.sectionDictionary, value, 'name_ru');
    }

    translateRate(value: AnalystRate): string {
        return findValueInDictionary(this.rateDictionary, value, 'name_ru');
    }

    translateReason(value: AnalystReason): string {
        return findValueInDictionary(this.reasonDictionary, value, 'name_ru');
    }

    submitCreateFeedback(): void {
        const feedbacks = this.form.controls.feedbacks.controls.map(value =>
            <AnalyticAgentFeedback> {
                reportSection: value.controls.section?.value,
                rate: value.controls.rate.value,
                reason: value.controls.reason.value,
            });

        this.analyticAgentApiService?.createIncidentReportFeedback(this.dialogData?.report?.id, {
            feedbacks: feedbacks
        }).pipe().subscribe({
            next: (response: AnalyticAgentReportResponse) => {
                this.dialogRef.close(response);
                this.snackBarService.showSnackBar('i18n_af_snackBarMessageAnalyticReportFeedbackCreated#' + response?.reportType);
            },
            error: err => {
                this.dialogRef.close();
                this.snackBarService.showSnackBar(
                    'i18n_af_snackBarMessageFailedToRateAnalyticReport#' + this.dialogData?.report?.reportType,
                    'error',
                    false
                );
            }
        });
    }

    trackByFeedbackFormIndex: TrackByFunction<FormGroup<AnalyticAgentReportFeedbackForm>> = (index: number) => index;

    trackByFeedbackIndex: TrackByFunction<AnalyticAgentReportFeedbackResponse> = (index: number) => index;

    private buildForm(): void {
        this.form = this.formBuilder.group({
            feedbacks: this.formBuilder.array([] as FormGroup<AnalyticAgentReportFeedbackForm>[])
        });

        if (this.dialogData?.report?.reportType == AnalyticAgentReportType.PDF) {
            this.sections.forEach(section => {
                const feedbackForm = this.formBuilder.group({
                    section: new FormControl(section),
                    rate: new FormControl(null, [Validators.required]),
                    reason: new FormControl(null, [Validators.required]),
                });
                this.form.controls.feedbacks.push(feedbackForm);
            });
        } else {
            this.form.controls.feedbacks.push(
                this.formBuilder.group({
                    section: new FormControl(null),
                    rate: new FormControl(null, [Validators.required]),
                    reason: new FormControl(null, [Validators.required]),
                })
            );
        }
    }
}

export interface AnalyticAgentReportFeedbackDialogData {
    report: AnalyticAgentReportResponse;
    readonly: boolean;
}

interface AnalyticAgentReportFeedbackForm {
    section: FormControl<AnalystSection>;
    rate: FormControl<AnalystRate>;
    reason: FormControl<AnalystReason>;
}

export interface AnalyticAgentReportFeedbacksForm {
    feedbacks: FormArray<FormGroup<AnalyticAgentReportFeedbackForm>>;
}
